import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { toast } from 'react-toastify';

import useMailerDriver from 'src/modules/mailer/hooks/useMailerDriver';
import useMailerSettingsService from 'src/modules/mailer/hooks/useMailerSettingsService';
import {
    applyMailerFormErrors,
    buildMailerPayload,
    defaultAuthModeOut,
    extractApiMessage,
    extractMailerFormErrors,
    getMailboxAccountChange,
    getMailboxAccountConfirmResult,
    hasIncomingSecretFormError,
    hasMailerHostFormErrors,
    isConnectMicrosoftHint,
    isCustomProvider,
    isMicrosoftProvider,
    isOtherProvider,
    mapMailerSettingsMessage,
    normalizePresets,
    pickHostFields,
    shouldSyncUsernameWithEmail,
    withIncompleteEntraFormErrors,
} from '../helpers';

export const MAILER_SETTINGS_QUERY_KEY = 'mailer-fetch-settings';
export const MAILER_PRESETS_QUERY_KEY = 'mailer-fetch-presets';

const applyIncomingFlag = (data = {}) => {
    const hasIncomingFlag =
        data?.imapSyncEnabled !== undefined || data?.enableIncoming !== undefined;

    if (!hasIncomingFlag) {
        return true;
    }

    return (
        data?.imapSyncEnabled === true ||
        data?.imapSyncEnabled === '1' ||
        data?.enableIncoming === true
    );
};

const useEmailSettings = ({
    t,
}) => {
    const { active: activeModule, settings } = useMailerDriver();
    const service = useMailerSettingsService();
    const formRef = useRef();
    const [initialSettings, setInitialSettings] = useState();
    const [defaultSettings, setDefaultSettings] = useState({});
    const [provider, setProvider] = useState('microsoft');
    const [imapSyncEnabled, setImapSyncEnabled] = useState(true);
    const [incomingSameAsSmtp, setIncomingSameAsSmtp] = useState(true);
    const [showAdvanced, setShowAdvanced] = useState(false);
    const [testStatus, setTestStatus] = useState(null);
    const [testMessage, setTestMessage] = useState('');
    const [active, setActive] = useState(false);
    const [showToggleActivationModal, setShowToggleActivationModal] = useState(false);
    const [formKey, setFormKey] = useState(0);
    const [mailboxChange, setMailboxChange] = useState(null);
    const pendingMailboxChangeRef = useRef(null);
    const lastEmailRef = useRef('');

    const applySettings = (data = {}) => {
        lastEmailRef.current = data?.emailAddress || '';
        setInitialSettings(data);
        setDefaultSettings(data);
        setActive(!!data?.isActive);
        setProvider(data?.provider || 'microsoft');
        setImapSyncEnabled(applyIncomingFlag(data));
        setShowAdvanced((data?.provider || 'microsoft') === 'custom');
        setIncomingSameAsSmtp(true);
        setTestStatus(null);
        setTestMessage('');
    };

    const {
        data: settingsData,
        isFetching: isFetchingSettings,
        refetch: refetchSettings,
    } = useQuery({
        queryKey: [settings.settingsQueryKey || MAILER_SETTINGS_QUERY_KEY, activeModule],
        queryFn: async () => {
            return await service.fetchMailerSettings();
        },
        cacheTime: 0,
        refetchOnWindowFocus: false,
        onError: (error) => {
            if (process.env.NODE_ENV === 'development') console.error(error);

            toast.error(
                t(extractApiMessage(error) || 'An error occurred while fetching settings.')
            );
        },
    });

    const {
        data: presetsResponse,
    } = useQuery({
        queryKey: [MAILER_PRESETS_QUERY_KEY, activeModule],
        queryFn: async () => {
            return await service.fetchPresets();
        },
        cacheTime: 0,
        refetchOnWindowFocus: false,
        onError: (error) => {
            if (process.env.NODE_ENV === 'development') {
                console.error(error);
            }
        },
    });

    useEffect(() => {
        if (settingsData) {
            applySettings(settingsData);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [settingsData]);

    const presets = useMemo(
        () => normalizePresets(presetsResponse),
        [presetsResponse]
    );

    useEffect(() => {
        const presetHosts = pickHostFields(presets[provider]);

        if (!Object.keys(presetHosts).length) {
            return;
        }

        setDefaultSettings((prev) => {
            const next = { ...prev };
            let changed = false;

            Object.entries(presetHosts).forEach(([key, value]) => {
                if (
                    value !== undefined &&
                    value !== '' &&
                    (next[key] === undefined || next[key] === null || next[key] === '')
                ) {
                    next[key] = value;
                    changed = true;
                }
            });

            return changed ? next : prev;
        });
    }, [presets, provider]);

    const isFetching = isFetchingSettings && !initialSettings;
    const isMicrosoft = isMicrosoftProvider(provider);
    const isCustom = isCustomProvider(provider);
    const isOther = isOtherProvider(provider);
    const showHostFields = isCustom || showAdvanced;
    const isFirstTime = !initialSettings?.emailAddress && !initialSettings?.smtpUsername;
    const secretRequired = isFirstTime;
    const incomingSecretRequired = isOther &&
        imapSyncEnabled &&
        !incomingSameAsSmtp &&
        isFirstTime;

    const showMailerApiError = (error, fallbackKey) => {
        const rawMessage = extractApiMessage(error);
        const fields = withIncompleteEntraFormErrors(
            extractMailerFormErrors(error),
            rawMessage,
            t
        );

        if (hasMailerHostFormErrors(fields)) {
            setShowAdvanced(true);
        }

        if (hasIncomingSecretFormError(fields)) {
            setIncomingSameAsSmtp(false);
        }

        const delay =
            hasMailerHostFormErrors(fields) || hasIncomingSecretFormError(fields)
                ? 50
                : 0;

        window.setTimeout(() => {
            applyMailerFormErrors(formRef.current, fields);
        }, delay);

        const message =
            mapMailerSettingsMessage(rawMessage, t) ||
            rawMessage ||
            t(fallbackKey);

        toast.error(message);

        return message;
    };

    const submitMutation = useMutation({
        mutationFn: async (payload) => {
            return await service.saveMailerSettings(payload);
        },
        onSuccess: (payload, variables) => {
            const change = getMailboxAccountChange(payload);
            const saved = payload?.data || payload;
            const confirmed = !!variables?.confirmMailboxChange;

            setTestStatus(null);

            if (change && !confirmed) {
                pendingMailboxChangeRef.current = {
                    payload: variables,
                    change,
                };
                setMailboxChange(change);
                toast.success(t('Settings saved successfully.'));
                refetchSettings();
                return;
            }

            if (confirmed) {
                const result = getMailboxAccountConfirmResult(payload);
                const pending = pendingMailboxChangeRef.current;
                const newEmail =
                    pending?.change?.newEmail ||
                    saved?.mailboxAccountEmail ||
                    saved?.emailAddress ||
                    variables?.mailer_setting?.emailAddress;
                const reused =
                    result.reused !== null
                        ? result.reused
                        : !!pending?.change?.reused;

                pendingMailboxChangeRef.current = null;
                setMailboxChange(null);

                if (reused) {
                    toast.success(
                        t('{{email}} was used before. Previous emails are restored from SIP.', {
                            email: newEmail,
                        })
                    );
                } else {
                    toast.success(
                        t('Inbox now uses {{email}}. Mail for the previous address stays in SIP.', {
                            email: newEmail,
                        })
                    );
                }

                refetchSettings().then((query) => {
                    if (query?.data) {
                        applySettings(query.data);
                    }
                    setFormKey((key) => key + 1);
                });

                if (!active) {
                    setShowToggleActivationModal('activation_after_save');
                }

                return;
            }

            toast.success(t('Settings saved successfully.'));
            refetchSettings();

            if (!active) {
                setShowToggleActivationModal('activation_after_save');
            }

            if (saved?.oauthConnected === false && defaultSettings?.oauthConnected) {
                toast.info(t('You will need to Connect Microsoft again after this change.'));
            }
        },
        onError: (error) => {
            if (process.env.NODE_ENV === 'development') console.error(error);

            showMailerApiError(error, 'An error occurred while saving settings.');
        },
    });

    const toggleActivationMutation = useMutation({
        mutationFn: async (payload) => {
            return await service.toggleMailerActivation(payload);
        },
        onSuccess: () => {
            toast.success(t(`Settings ${active ? 'deactivated' : 'activated'} successfully.`));
            setActive(!active);
        },
        onError: (error) => {
            if (process.env.NODE_ENV === 'development') console.error(error);

            toast.error(
                t(extractApiMessage(error) || 'An error occurred while toggling activation.')
            );
        },
    });

    const testConnectionMutation = useMutation({
        mutationFn: async (payload) => {
            return await service.testConnection(payload);
        },
        onSuccess: (payload) => {
            const hint = extractApiMessage(payload);

            if (isConnectMicrosoftHint(hint)) {
                setTestStatus('info');
                setTestMessage(hint);
                toast.info(hint);
                return;
            }

            setTestStatus('success');
            setTestMessage('');
            toast.success(
                t('Connection successful. Emails can be sent with this configuration.')
            );
        },
        onError: (error) => {
            if (process.env.NODE_ENV === 'development') console.error(error);

            const rawMessage = extractApiMessage(error);

            if (isConnectMicrosoftHint(rawMessage)) {
                setTestStatus('info');
                setTestMessage(rawMessage);
                toast.info(rawMessage);
                return;
            }

            const message = showMailerApiError(
                error,
                'Cannot reach SMTP. Check password / firewall. Save is still allowed.'
            );

            setTestStatus('error');
            setTestMessage(message);
        },
    });

    const getHostFallbacks = () => ({
        ...pickHostFields(presets[provider]),
        ...pickHostFields(defaultSettings),
        smtpUsername: defaultSettings?.smtpUsername,
        incomingUsername: defaultSettings?.incomingUsername,
        oauthTenantId: defaultSettings?.oauthTenantId,
        oauthClientId: defaultSettings?.oauthClientId,
        authModeOut: defaultSettings?.authModeOut,
    });

    const getPayloadFromForm = () => {
        const values = formRef.current?.getValues?.() || {};

        return buildMailerPayload(values, {
            provider,
            imapSyncEnabled,
            incomingSameAsSmtp,
            fallbacks: getHostFallbacks(),
        });
    };

    const handleProviderChange = (nextProvider) => {
        setProvider(nextProvider);
        setShowAdvanced(nextProvider === 'custom');
        setTestStatus(null);

        const presetHosts = pickHostFields(presets[nextProvider]);

        setDefaultSettings((prev) => ({
            ...prev,
            smtpServer: presetHosts.smtpServer || '',
            smtpPort: presetHosts.smtpPort || '',
            incomingServer: presetHosts.incomingServer || '',
            incomingPort: presetHosts.incomingPort || '',
            incomingEncryption: presetHosts.incomingEncryption || '',
            authModeOut: defaultAuthModeOut(nextProvider),
        }));
        setFormKey((key) => key + 1);
    };

    const handleEmailAddressChange = (event) => {
        const nextEmail = event?.target?.value ?? '';
        const previousEmail = lastEmailRef.current;
        const values = formRef.current?.getValues?.() || {};

        lastEmailRef.current = nextEmail;

        setDefaultSettings((prev) => {
            const smtpUsername = values.smtpUsername ?? prev.smtpUsername ?? '';
            const incomingUsername = values.incomingUsername ?? prev.incomingUsername ?? '';
            const syncSmtp = shouldSyncUsernameWithEmail(smtpUsername, previousEmail);
            const syncImap = shouldSyncUsernameWithEmail(incomingUsername, previousEmail);

            return {
                ...prev,
                emailAddress: nextEmail,
                ...(syncSmtp ? { smtpUsername: nextEmail } : {}),
                ...(syncImap ? { incomingUsername: nextEmail } : {}),
            };
        });
    };

    const handleSubmit = (_e, values) => {
        const payload = buildMailerPayload(values, {
            provider,
            imapSyncEnabled,
            incomingSameAsSmtp,
            fallbacks: getHostFallbacks(),
        });
        const savedProvider = initialSettings?.provider || '';

        if (savedProvider && provider !== savedProvider) {
            const change = {
                previousEmail: (initialSettings?.emailAddress || '').trim(),
                newEmail: (payload.mailer_setting?.emailAddress || '').trim(),
                previousIncomingUsername: (initialSettings?.incomingUsername || '').trim(),
                newIncomingUsername: (payload.mailer_setting?.incomingUsername || '').trim(),
                reused: false,
                warning: '',
            };
            pendingMailboxChangeRef.current = { payload, change };
            setMailboxChange(change);
            return;
        }

        submitMutation.mutate(payload);
    };

    const handleTestConnection = () => {
        testConnectionMutation.mutate(getPayloadFromForm());
    };

    const handleCancelMailboxChange = () => {
        const pending = pendingMailboxChangeRef.current;
        const previousEmail = pending?.change?.previousEmail;
        const previousIncomingUsername = pending?.change?.previousIncomingUsername;

        pendingMailboxChangeRef.current = null;
        setMailboxChange(null);

        refetchSettings().then((query) => {
            const data = query?.data || {};

            applySettings({
                ...data,
                emailAddress: previousEmail || data.emailAddress,
                incomingUsername: previousIncomingUsername || data.incomingUsername,
            });
            setFormKey((key) => key + 1);
        });
    };

    const handleConfirmMailboxChange = () => {
        const pending = pendingMailboxChangeRef.current;

        if (!pending?.payload) {
            setMailboxChange(null);
            return;
        }

        submitMutation.mutate({
            ...pending.payload,
            confirmMailboxChange: true,
        });
    };

    const refreshMailerSettings = (overrides = {}) => {
        if (overrides && Object.keys(overrides).length) {
            applySettings({
                ...defaultSettings,
                ...overrides,
            });
            setFormKey((key) => key + 1);
        }

        return refetchSettings().then((query) => {
            if (query?.data) {
                applySettings({
                    ...query.data,
                    ...overrides,
                });
                setFormKey((key) => key + 1);
            }

            return query;
        });
    };

    return {
        formRef,
        formKey,
        defaultSettings,
        provider,
        presets,
        imapSyncEnabled,
        setImapSyncEnabled,
        incomingSameAsSmtp,
        setIncomingSameAsSmtp,
        showAdvanced,
        setShowAdvanced,
        testStatus,
        testMessage,
        active,
        showToggleActivationModal,
        setShowToggleActivationModal,
        isFetching,
        isMicrosoft,
        isCustom,
        isOther,
        showHostFields,
        isFirstTime,
        secretRequired,
        incomingSecretRequired,
        submitMutation,
        toggleActivationMutation,
        testConnectionMutation,
        handleProviderChange,
        handleEmailAddressChange,
        handleSubmit,
        handleTestConnection,
        mailboxChange,
        handleCancelMailboxChange,
        handleConfirmMailboxChange,
        refreshMailerSettings,
    };
};

export default useEmailSettings;
